import React, { useEffect, useRef, useMemo, useContext } from 'react';
import AgoraContext from '../../context/AgoraContext';

/**
 * AgoraVideoPlayer - A video player component for Agora video tracks
 * This is designed to mirror the VideoSDK VideoPlayer interface
 *
 * Usage (VideoSDK-compatible):
 *   <VideoPlayer participantId={id} type="video" />
 *   <VideoPlayer participantId={id} type="share" />
 *
 * Or direct track usage:
 *   <VideoPlayer videoTrack={track} />
 */
const AgoraVideoPlayer = ({
  // VideoSDK-compatible props
  participantId,
  type = 'video', // "video" or "share"
  containerStyle = {},
  className = '',
  classNameVideo = '',
  videoStyle = {},
  // Direct track props (alternative usage)
  videoTrack: directVideoTrack,
  audioTrack: directAudioTrack,
  playAudio = false, // Don't play audio by default (handled by separate audio element)
  playVideo = true,
  ...props
}) => {
  const videoRef = useRef(null);

  // Get context data - use useContext directly to handle null case gracefully
  const contextData = useContext(AgoraContext);

  // Get participant data if participantId is provided
  // IMPROVED: Try multiple lookup strategies to handle type mismatches
  // (participantId might be string or number, Map keys might be either)
  const participant = useMemo(() => {
    if (participantId == null || !contextData?.participants) return null;

    const participants = contextData.participants;

    // Try direct lookup first
    let result = participants.get(participantId);
    if (result) return result;

    // Try numeric version (remote participants are stored with numeric UIDs)
    const numericId = Number(participantId);
    if (!isNaN(numericId)) {
      result = participants.get(numericId);
      if (result) return result;
    }

    // Try string version
    result = participants.get(String(participantId));
    if (result) return result;

    // Last resort: iterate and compare as strings
    // But prioritize remote participants over local to avoid hash collisions
    const participantIdStr = String(participantId);
    for (const [key, value] of participants) {
      if (value?.isLocal) continue; // Check remote first
      if (String(key) === participantIdStr || String(value?.id) === participantIdStr) {
        return value;
      }
    }

    // Finally check local participant
    for (const [key, value] of participants) {
      if (!value?.isLocal) continue;
      if (String(key) === participantIdStr || String(value?.id) === participantIdStr) {
        return value;
      }
    }

    return null;
  }, [participantId, contextData?.participants]);

  // For local participant, get the track from context directly
  const localVideoTrack = contextData?.localVideoTrack;
  const localScreenTrack = contextData?.localScreenTrack;
  const isLocal = participant?.isLocal;

  // Determine which video track to use (prefer Agora track for play())
  const videoTrack = useMemo(() => {
    // Direct track takes priority
    if (directVideoTrack) return directVideoTrack;

    if (!participant) return null;

    // For share type, use screenShareStream
    if (type === 'share') {
      const shareStream = participant.screenShareStream;
      // Prefer agoraTrack for play() method
      if (shareStream?.agoraTrack) {
        return shareStream.agoraTrack;
      }
      return null;
    }

    // For video type, use webcamStream
    const webcamStream = participant.webcamStream;
    // Prefer agoraTrack for play() method
    if (webcamStream?.agoraTrack) {
      return webcamStream.agoraTrack;
    }

    return null;
  }, [directVideoTrack, participant, type]);

  // Final track to play
  const finalTrack = useMemo(() => {
    if (directVideoTrack) return directVideoTrack;

    if (isLocal) {
      if (type === 'share') {
        return localScreenTrack;
      }
      return localVideoTrack;
    }

    return videoTrack;
  }, [directVideoTrack, isLocal, type, localVideoTrack, localScreenTrack, videoTrack]);

  // Play video track
  useEffect(() => {
    if (!videoRef.current || !playVideo) return;

    // Clear previous content
    videoRef.current.innerHTML = '';

    if (!finalTrack) return;

    let retryTimer = null;
    let observer = null;

    // Grid recorder: 4 cells may want to display the SAME track (screen
    // share especially — it's one track shared by all 4 layouts). Agora's
    // `track.play(element)` ATTACHES the track to a specific DOM element
    // and moving it detaches from earlier elements. Only the last cell
    // would show video, the rest go black.
    //
    // Fix: for recorder mode, use the underlying MediaStreamTrack inside
    // a fresh MediaStream + <video srcObject>. WebRTC supports the same
    // track in multiple MediaStreams simultaneously — no track-stealing.
    // Normal meetings keep Agora's native play() so we don't touch the
    // non-recorder path at all.
    const isRecorderPage = typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('recorder') === 'true';

    const playIntoContainer = () => {
      if (!videoRef.current) return;
      videoRef.current.innerHTML = '';

      // Resolve the underlying MediaStreamTrack for the multi-element path.
      const underlyingTrack = finalTrack instanceof MediaStreamTrack
        ? finalTrack
        : (typeof finalTrack.getMediaStreamTrack === 'function'
            ? finalTrack.getMediaStreamTrack()
            : null);

      const useSrcObjectPath = isRecorderPage && underlyingTrack;

      if (useSrcObjectPath) {
        const stream = new MediaStream([underlyingTrack]);
        const videoEl = document.createElement('video');
        videoEl.srcObject = stream;
        videoEl.autoplay = true;
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.style.width = '100%';
        videoEl.style.height = '100%';
        videoEl.style.objectFit = type === 'share' ? 'contain' : 'cover';
        if (isLocal && type !== 'share') {
          videoEl.style.transform = 'scaleX(-1)';
        }
        videoRef.current.appendChild(videoEl);
        videoEl.play().catch((err) => {
          console.warn('[AgoraVideoPlayer] srcObject play() failed:', err?.message);
        });
      } else if (typeof finalTrack.play === 'function') {
        try {
          // For local webcam, force mirror=true so the user always sees
          // selfie view. Agora's default differs between camera tracks
          // (mirror=true) and the CustomVideoTrack produced by the VB
          // processor (mirror=false), which caused the flip after VB kicks in.
          // Screen shares must not be mirrored.
          if (isLocal && type !== 'share') {
            finalTrack.play(videoRef.current, { mirror: true, fit: 'cover' });
          } else if (type === 'share') {
            finalTrack.play(videoRef.current, { mirror: false, fit: 'contain' });
          } else {
            finalTrack.play(videoRef.current, { mirror: false, fit: 'cover' });
          }
        } catch (err) {
          console.error('Failed to play video track:', err);
        }
      } else if (finalTrack instanceof MediaStreamTrack) {
        const stream = new MediaStream([finalTrack]);
        const videoEl = document.createElement('video');
        videoEl.srcObject = stream;
        videoEl.autoplay = true;
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.style.width = '100%';
        videoEl.style.height = '100%';
        videoEl.style.objectFit = type === 'share' ? 'contain' : 'cover';

        videoRef.current.appendChild(videoEl);
        videoEl.play().catch(console.error);
      }
    };

    // Play immediately
    playIntoContainer();

    // If container had 0 dimensions (e.g. late joiner before layout), retry with backoff
    const rect = videoRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      let retryCount = 0;
      const retryPlay = () => {
        if (!videoRef.current || retryCount >= 5) return;
        const r = videoRef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          playIntoContainer();
        } else {
          retryCount++;
          retryTimer = setTimeout(retryPlay, 500 * retryCount);
        }
      };
      retryTimer = setTimeout(retryPlay, 500);
    }

    // Use MutationObserver to persistently enforce object-fit: contain on all videos.
    // Agora SDK applies inline object-fit: cover which we need to override so that:
    // - Portrait video (mobile users) shows as vertical with side letterboxing (like Zoom)
    // - Landscape video (desktop users) fills the 16:9 tile naturally
    if (videoRef.current) {
      const enforceContain = () => {
        const videoEl = videoRef.current?.querySelector('video');
        if (videoEl && videoEl.style.objectFit !== 'contain') {
          videoEl.style.objectFit = 'contain';
        }
      };
      enforceContain();

      observer = new MutationObserver(enforceContain);
      observer.observe(videoRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    // Re-claim the track when another component (the SettingsModal Visual
    // Effects preview) calls track.play() into its own container — Agora's
    // play() detaches the track from the previous element, so this tile
    // would otherwise stay blank after the modal closes.
    const handleAgoraDetach = () => {
      if (!videoRef.current) return;
      playIntoContainer();
    };
    window.addEventListener('agora-preview-detached', handleAgoraDetach);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (observer) observer.disconnect();
      window.removeEventListener('agora-preview-detached', handleAgoraDetach);
      if (videoRef.current) {
        videoRef.current.innerHTML = '';
      }
    };
  }, [finalTrack, playVideo]);

  // Handle audio track separately
  const audioTrack = directAudioTrack;
  useEffect(() => {
    if (audioTrack && playAudio) {
      if (typeof audioTrack.play === 'function') {
        audioTrack.play();
      }
    }
  }, [audioTrack, playAudio]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        ...containerStyle,
      }}
      className={className}
      {...props}
    >
      <div
        ref={videoRef}
        className={classNameVideo}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: 'transparent',
          ...videoStyle
        }}
        data-participant-id={participantId}
      />
    </div>
  );
};

export default AgoraVideoPlayer;
